import { useEffect, useState, useMemo, type CSSProperties, type FormEvent } from "react";
import {
  listArticles,
  saveArticle,
  deleteArticle,
  listDispatchRuns,
  type Article,
  type DispatchRun,
} from "../../lib/api";
import PublishingStudioTabs from "../../components/admin/PublishingStudioTabs";

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function AdminArticles() {
  const [activeTab, setActiveTab] = useState<"articles" | "dispatches">("articles");
  const [loading, setLoading] = useState(true);

  // Articles state
  const [articles, setArticles] = useState<Article[]>([]);
  const [articleSearch, setArticleSearch] = useState("");
  const [articleTypeFilter, setArticleTypeFilter] = useState("all");
  const [articleStatusFilter, setArticleStatusFilter] = useState("all");
  const [editingArticle, setEditingArticle] = useState<Partial<Article> | null>(null);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [savingArticle, setSavingArticle] = useState(false);

  // Dispatch run history state (read-only — the discovery pipeline's run log)
  const [dispatchRuns, setDispatchRuns] = useState<DispatchRun[]>([]);
  const [dispatchSearch, setDispatchSearch] = useState("");

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isArticleModalOpen) setIsArticleModalOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isArticleModalOpen]);

  const loadArticlesData = async () => {
    setLoading(true);
    try {
      const data = await listArticles();
      setArticles(data || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load articles", "err");
    } finally {
      setLoading(false);
    }
  };

  const loadDispatchesData = async () => {
    setLoading(true);
    try {
      const data = await listDispatchRuns();
      setDispatchRuns(data || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load dispatch runs", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "articles") {
      loadArticlesData();
    } else {
      loadDispatchesData();
    }
  }, [activeTab]);

  const filteredArticles = useMemo(() => {
    let list = articles;
    if (articleTypeFilter !== "all") {
      list = list.filter((a) => (a.article_type || "").toLowerCase() === articleTypeFilter);
    }
    if (articleStatusFilter !== "all") {
      list = list.filter((a) => (a.status || "").toLowerCase() === articleStatusFilter);
    }
    if (articleSearch.trim()) {
      const q = articleSearch.toLowerCase().trim();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          (a.excerpt && a.excerpt.toLowerCase().includes(q)) ||
          a.slug.toLowerCase().includes(q)
      );
    }
    return list;
  }, [articles, articleTypeFilter, articleStatusFilter, articleSearch]);

  const filteredDispatchRuns = useMemo(() => {
    let list = dispatchRuns;
    if (dispatchSearch.trim()) {
      const q = dispatchSearch.toLowerCase().trim();
      list = list.filter((r) => (r.status || "").toLowerCase().includes(q));
    }
    return list;
  }, [dispatchRuns, dispatchSearch]);

  const handleNewArticle = () => {
    setEditingArticle({
      title: "",
      slug: "",
      article_type: "journal",
      status: "draft",
      excerpt: "",
      content_markdown: "",
      cover_image_url: "",
      reading_time_minutes: 3,
      tags: ["whiskey", "tasting"],
      published_at: null,
    });
    setIsArticleModalOpen(true);
  };

  const handleEditArticle = (a: Article) => {
    setEditingArticle({ ...a });
    setIsArticleModalOpen(true);
  };

  const handleSaveArticle = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingArticle || !editingArticle.title) return;
    setSavingArticle(true);
    try {
      const payload: Partial<Article> = {
        ...editingArticle,
        slug: editingArticle.slug || slugify(editingArticle.title),
        published_at:
          editingArticle.status === "published"
            ? editingArticle.published_at || new Date().toISOString()
            : null,
      };
      await saveArticle(payload);
      showToast(`✓ Article "${payload.title}" saved!`);
      setIsArticleModalOpen(false);
      await loadArticlesData();
    } catch (err: any) {
      showToast(err.message || "Failed to save article", "err");
    } finally {
      setSavingArticle(false);
    }
  };

  const handleDeleteArticle = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await deleteArticle(id);
      showToast(`✓ Deleted "${title}"`);
      setIsArticleModalOpen(false);
      await loadArticlesData();
    } catch (err: any) {
      showToast(err.message || "Failed to delete article", "err");
    }
  };

  return (
    <div style={styles.container}>
      <PublishingStudioTabs activeTab="articles" badgeCounts={{ articles: articles.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Editorial Content &amp; Dispatches</h1>
          <p style={styles.subtitle}>
            Publish speakeasy journal articles, tasting notes, and review automated distillery intelligence dispatches.
          </p>
        </div>
        {activeTab === "articles" && (
          <button type="button" style={styles.btnPrimary} onClick={handleNewArticle}>
            + New Article
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={styles.tabsRow}>
        <button
          type="button"
          style={activeTab === "articles" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("articles")}
        >
          Journal Articles ({articles.length})
        </button>
        <button
          type="button"
          style={activeTab === "dispatches" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("dispatches")}
        >
          Distillery Dispatches
        </button>
      </div>

      {/* ── Tab 1: Articles ─────────────────────────────────────────────────── */}
      {activeTab === "articles" && (
        <div>
          <div style={styles.filterRow}>
            <input
              type="text"
              placeholder="Search articles by title, excerpt, slug…"
              value={articleSearch}
              onChange={(e) => setArticleSearch(e.target.value)}
              style={styles.searchInput}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={articleTypeFilter}
                onChange={(e) => setArticleTypeFilter(e.target.value)}
                style={styles.select}
              >
                <option value="all">All Types</option>
                <option value="journal">Journal</option>
                <option value="education">Education</option>
              </select>
              <select
                value={articleStatusFilter}
                onChange={(e) => setArticleStatusFilter(e.target.value)}
                style={styles.select}
              >
                <option value="all">All Statuses</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div style={styles.loader}>Loading articles…</div>
          ) : filteredArticles.length === 0 ? (
            <div style={styles.emptyState}>No articles found matching the current filter.</div>
          ) : (
            <div style={styles.articleGrid}>
              {filteredArticles.map((a) => (
                <div
                  key={a.id}
                  style={styles.articleCard}
                  onClick={() => handleEditArticle(a)}
                >
                  {a.cover_image_url && (
                    <img src={a.cover_image_url} alt="" style={styles.coverImg} loading="lazy" />
                  )}
                  <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={styles.typePill}>{(a.article_type || "journal").toUpperCase()}</span>
                      <span
                        style={{
                          ...styles.statusPill,
                          background: a.status === "published" ? "rgba(39, 174, 96, 0.1)" : "rgba(176, 160, 140, 0.1)",
                          color: a.status === "published" ? "#27ae60" : "var(--muted)",
                        }}
                      >
                        {(a.status || "draft").toUpperCase()}
                      </span>
                    </div>
                    <h3 style={styles.cardTitle}>{a.title}</h3>
                    {a.excerpt && <p style={styles.cardExcerpt}>{a.excerpt}</p>}
                    <div style={styles.cardMeta}>
                      <span>{a.reading_time_minutes || 3} min read</span>
                      <span>Slug: <code>{a.slug}</code></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab 2: Dispatch run history (read-only) ────────────────────────── */}
      {activeTab === "dispatches" && (
        <div>
          <div style={styles.filterRow}>
            <input
              type="text"
              placeholder="Filter runs by status (completed, failed…)"
              value={dispatchSearch}
              onChange={(e) => setDispatchSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {loading ? (
            <div style={styles.loader}>Loading dispatch runs…</div>
          ) : filteredDispatchRuns.length === 0 ? (
            <div style={styles.emptyState}>No discovery pipeline runs recorded yet.</div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {filteredDispatchRuns.map((r) => (
                <div key={r.id} style={styles.dispatchCard}>
                  <div style={styles.dspHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={styles.dspType}>{(r.status || "unknown").replace("_", " ")}</span>
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>
                        {r.distilleries_queried ?? 0} distilleries queried
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: "var(--muted)" }}>
                      {new Date(r.started_at).toLocaleString()}
                      {r.completed_at &&
                        ` → ${Math.max(1, Math.round((new Date(r.completed_at).getTime() - new Date(r.started_at).getTime()) / 60000))} min`}
                    </span>
                  </div>

                  <p style={styles.dspBody}>
                    Articles: {r.articles_found ?? 0} found · {r.articles_verified ?? 0} verified ·{" "}
                    {r.articles_approved ?? 0} approved · {r.articles_rejected ?? 0} rejected
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Article Modal */}
      {isArticleModalOpen && editingArticle && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingArticle.id ? `Edit: ${editingArticle.title}` : "New Journal Article"}
              </h2>
              <button
                type="button"
                style={styles.btnIcon}
                onClick={() => setIsArticleModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveArticle} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Article Title *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. The Architecture of Bottled-in-Bond: 1897 to Today"
                    value={editingArticle.title || ""}
                    onChange={(e) => {
                      const title = e.target.value;
                      setEditingArticle({
                        ...editingArticle,
                        title,
                        slug: editingArticle.slug || slugify(title),
                      });
                    }}
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Slug *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. architecture-of-bottled-in-bond"
                    value={editingArticle.slug || ""}
                    onChange={(e) =>
                      setEditingArticle({ ...editingArticle, slug: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Article Type</label>
                  <select
                    value={editingArticle.article_type || "journal"}
                    onChange={(e) =>
                      setEditingArticle({ ...editingArticle, article_type: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="journal">Speakeasy Journal</option>
                    <option value="education">Education &amp; Tasting Guide</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Status</label>
                  <select
                    value={editingArticle.status || "draft"}
                    onChange={(e) =>
                      setEditingArticle({ ...editingArticle, status: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="draft">Draft (Private)</option>
                    <option value="published">Published (Public)</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Est. Reading Time (Mins)</label>
                  <input
                    type="number"
                    min="1"
                    value={editingArticle.reading_time_minutes || 3}
                    onChange={(e) =>
                      setEditingArticle({
                        ...editingArticle,
                        reading_time_minutes: parseInt(e.target.value, 10) || 1,
                      })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Cover Image URL</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={editingArticle.cover_image_url || ""}
                    onChange={(e) =>
                      setEditingArticle({ ...editingArticle, cover_image_url: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Summary Excerpt</label>
                  <textarea
                    rows={2}
                    placeholder="Short 1–2 sentence overview for cards and meta descriptions..."
                    value={editingArticle.excerpt || ""}
                    onChange={(e) =>
                      setEditingArticle({ ...editingArticle, excerpt: e.target.value })
                    }
                    style={styles.textarea}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Markdown Content</label>
                  <textarea
                    rows={12}
                    placeholder="# Introduction\n\nArticle body in markdown..."
                    value={editingArticle.content_markdown || ""}
                    onChange={(e) =>
                      setEditingArticle({
                        ...editingArticle,
                        content_markdown: e.target.value,
                      })
                    }
                    style={{ ...styles.textarea, fontFamily: "Georgia, serif", fontSize: 14 }}
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div style={styles.modalActions}>
                {editingArticle.id && (
                  <button
                    type="button"
                    style={styles.btnDanger}
                    onClick={() =>
                      handleDeleteArticle(editingArticle.id!, editingArticle.title || "Article")
                    }
                  >
                    Delete Article
                  </button>
                )}
                <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
                  <button
                    type="button"
                    style={styles.btnSecondary}
                    onClick={() => setIsArticleModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={styles.btnPrimary} disabled={savingArticle}>
                    {savingArticle ? "Saving…" : "Save Article"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  tabsRow: { display: "flex", gap: 8, borderBottom: "1px solid var(--border)", paddingBottom: 8, marginBottom: 20 },
  tab: { background: "transparent", border: 0, color: "var(--muted)", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 },
  tabActive: { background: "rgba(249,175,54,0.15)", border: 0, color: "var(--gold)", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 },
  filterRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  searchInput: { flex: 1, minWidth: 240, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  select: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  articleGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 },
  articleCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column" },
  coverImg: { width: "100%", height: 160, objectFit: "cover" },
  typePill: { fontSize: 10, fontWeight: 800, color: "var(--gold)", letterSpacing: 0.5 },
  statusPill: { fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4 },
  cardTitle: { fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)", lineHeight: 1.3 },
  cardExcerpt: { fontSize: 12, color: "var(--muted)", margin: 0, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },
  cardMeta: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 8, marginTop: "auto", fontSize: 11, color: "var(--muted)" },
  dispatchCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 16 },
  dspHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  dspDistillery: { fontSize: 11, fontWeight: 800, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5 },
  dspType: { fontSize: 10, fontWeight: 700, background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4, textTransform: "uppercase" },
  dspHeadline: { fontSize: 15, fontWeight: 700, margin: "0 0 6px", color: "var(--text)" },
  dspBody: { fontSize: 13, color: "var(--muted)", lineHeight: 1.5, margin: "0 0 12px" },
  dspActions: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  btnApprove: { background: "rgba(39,174,96,0.15)", border: "1px solid rgba(39,174,96,0.4)", color: "#27ae60", padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnSkip: { background: "transparent", border: "1px solid var(--border)", color: "var(--muted)", padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer" },
  sourceLink: { fontSize: 12, color: "var(--gold)", textDecoration: "none", marginLeft: "auto" },
  btnPrimary: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnDanger: { background: "transparent", color: "#E05040", border: "1px solid rgba(224,80,64,0.4)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnIcon: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 22, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 760, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  modalForm: { padding: 20, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5 },
  input: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  textarea: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13, resize: "vertical" },
  modalActions: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 16 },
};
